import { withFieldGroup } from '@massilia/features'
import { cn, Label, RadioGroup, RadioGroupItem } from '@massilia/ui'
import { format, isValid, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'

import type { CheckoutFormValues } from '../checkout-form.schema'

interface PaymentTimingSectionProps {
  /** ISO date string ("yyyy-MM-dd" or full ISO) — when the deposit must be received. */
  depositDeadline?: string
}

type PaymentTimingValues = Pick<CheckoutFormValues, 'paymentTiming'>

function parseDepositDeadline(raw?: string): Date | null {
  if (!raw) return null
  const parsed = parseISO(raw.length === 10 ? `${raw}T00:00:00` : raw)
  return isValid(parsed) ? parsed : null
}

export const PaymentTimingSection = withFieldGroup({
  defaultValues: {} as PaymentTimingValues,
  props: {} as PaymentTimingSectionProps,
  render: function PaymentTimingFields({ depositDeadline, group }) {
    const deadlineDate = parseDepositDeadline(depositDeadline)
    const isPayLaterAvailable = !!deadlineDate && deadlineDate.getTime() > Date.now()
    const formattedDeadline = deadlineDate
      ? format(deadlineDate, 'd MMMM yyyy', { locale: fr })
      : null

    return (
      <div className="flex w-full flex-col gap-3">
        <group.Subscribe selector={(state) => state.values.paymentTiming}>
          {(paymentTiming) => (
            <RadioGroup
              className="flex flex-col gap-0"
              onValueChange={(value) => {
                if (value !== 'PAY_NOW' && value !== 'PAY_LATER') return
                group.setFieldValue('paymentTiming', value)
              }}
              value={paymentTiming}
            >
              <div
                className={cn(
                  'border-gray-light-300 flex w-full flex-col border-t border-r border-l transition-all',
                  isPayLaterAvailable ? 'rounded-t-2xl' : 'rounded-2xl border-b',
                  paymentTiming === 'PAY_NOW' && 'border-brand-600 bg-brand-50 border-b'
                )}
              >
                <div className="flex items-start gap-2 p-4">
                  <RadioGroupItem className="mt-0.5" id="payment-timing-now" value="PAY_NOW" />
                  <Label
                    className="flex-1 text-sm font-semibold"
                    htmlFor="payment-timing-now"
                  >
                    Je paie maintenant
                  </Label>
                </div>
              </div>

              {isPayLaterAvailable && (
                <div
                  className={cn(
                    'border-gray-light-300 rounded-b-2xl border-r border-b border-l transition-all',
                    paymentTiming === 'PAY_LATER' && 'border-brand-600 bg-brand-50 border-t'
                  )}
                >
                  <div className="flex items-start gap-2 p-4">
                    <RadioGroupItem
                      className="mt-0.5"
                      id="payment-timing-later"
                      value="PAY_LATER"
                    />
                    <div className="flex flex-1 flex-col gap-1">
                      <Label
                        className="text-sm font-semibold"
                        htmlFor="payment-timing-later"
                      >
                        Je paie avant le {formattedDeadline}
                      </Label>
                      <div
                        className={cn(
                          'overflow-hidden transition-all duration-300 ease-in-out',
                          paymentTiming === 'PAY_LATER'
                            ? 'max-h-32 opacity-100'
                            : 'pointer-events-none max-h-0 opacity-0'
                        )}
                      >
                        <p className="text-gray-light-700 pt-1 text-xs">
                          Votre dossier sera réservé. Vous recevrez un lien de paiement valable
                          jusqu&apos;au {formattedDeadline}. Sans règlement, votre demande sera
                          annulée.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </RadioGroup>
          )}
        </group.Subscribe>
      </div>
    )
  },
})
